<template>
  <div class="max-w-[968px] mx-auto mt-16 md-1:mt-24 px-16 pb-72 md-1:pb-0">
    <provider-profile-action-bar
      :accountTypeInfo="accountTypeInfo"
      :profileAccountData="profileAccountData"
      :viewerAccountData="viewerAccountData"
      :favoritedSitter="favoritedSitter"
      @favoritedSitterChange="favoritedSitterChange"
    />
    <provider-profile-loading-skeleton v-if="loading" />
    <provider-profile-error-skeleton v-else-if="bannerType === BannerType.ACCOUNT_NOT_FOUND" />
    <div v-else class="flex flex-col gap-16 mt-24 md-1:flex-row md-1:gap-32">
      <div class="w-full top-128 self-start md-1:max-w-[45%] md-1:sticky">
        <provider-profile-sitter-snapshot
          :profileAccountData="profileAccountData"
          :reviewsData="reviewsData"
          :profileBelongsToViewer="profileBelongsToViewer"
          :iconListItems="snapshotIconListItems"
          :rateDisplay="rateDisplay"
          :accountTypeInfo="accountTypeInfo"
          :favoritedSitter="favoritedSitter"
          @favoritedSitterChange="favoritedSitterChange"
          @item-click="(item) => item.clickHandler()"
        />
      </div>
      <provider-profile-sections
        class="w-full md-1:max-w-[55%] mb-[160px] md-1:mb-0"
        :profileAccountData="profileAccountData"
        :reviewsData="reviewsData"
        :paramData="paramData"
        :profileBelongsToViewer="profileBelongsToViewer"
        @view-more-reviews="$emit('view-more-reviews')"
      />
    </div>
  </div>
</template>
<script lang="ts">
import pluralize from 'pluralize';
import { BannerType } from '../__types__/ErrorTypes';
import { FormattedCareType } from '../__types__/CareTypes';

import ProviderProfileActionBar from './ProviderProfileActionBar.vue';
import ProviderProfileLoadingSkeleton from './ProviderProfileLoadingSkeleton.vue';
import ProviderProfileErrorSkeleton from './ProviderProfileErrorSkeleton.vue';
import ProviderProfileSitterSnapshot from './ProviderProfileSitterSnapshot.vue';
import ProviderProfileSections from './ProviderProfileSections.vue';
import HeartOutlineIcon from '@/graphics/icons/HeartOutlineIcon.vue';
import PlaceOutlineIcon from '@/graphics/icons/PlaceOutlineIcon.vue';
import DollarIcon from '@/graphics/icons/DollarIcon.vue';
import LoggedInIcon from '@/graphics/icons/LoggedInIcon.vue';

export default {
  name: 'ProviderProfileContainer',
  components: {
    ProviderProfileActionBar,
    ProviderProfileLoadingSkeleton,
    ProviderProfileErrorSkeleton,
    ProviderProfileSitterSnapshot,
    ProviderProfileSections,
  },
  props: {
    profileAccountData: {
      default: () => ({}),
      type: Object,
    },
    viewerAccountData: {
      default: () => ({}),
      type: Object,
    },
    reviewsData: {
      default: () => ({}),
      type: Object,
    },
    accountTypeInfo: {
      default: () => ({}),
      type: Object,
    },
    loading: {
      type: Boolean,
      default: true,
    },
    bannerType: {
      type: String,
      default: BannerType.NONE,
    },
    paramData: {
      type: Object,
      default: () => ({}),
    },
  },
  data(): any {
    return {
      BannerType,
      favoritedSitter: this.profileAccountData?.favoritedSitter || false,
    };
  },
  computed: {
    profileData(): any {
      const { careType } = this.paramData;
      const formattedCareType = FormattedCareType[careType.toUpperCase()];
      return this.profileAccountData?.profiles[formattedCareType]?.profile;
    },
    profileBelongsToViewer(): boolean {
      return this.profileAccountData?.id === this.viewerAccountData?.id;
    },
    snapshotIconListItems(): any {
      return [
        {
          icon: HeartOutlineIcon,
          text: this.yearsExperienceDisplay,
          clickHandler: () => {
            console.log('Years of experience clicked');
          },
        },
        {
          icon: PlaceOutlineIcon,
          text: this.locationDisplay,
          clickHandler: () => {
            console.log('Location clicked');
          },
        },
        {
          icon: DollarIcon,
          text: this.rateDisplay,
          clickHandler: () => {
            console.log('Pay rate clicked');
          },
        },
        {
          icon: LoggedInIcon,
          text: this.loggedInDisplay,
        },
      ];
    },
    yearsExperienceDisplay(): string {
      const { yearsExperience } = this.profileData || {};
      if (!yearsExperience) return '';
      return `${yearsExperience} ${pluralize('year', yearsExperience)} of experience`;
    },
    locationDisplay(): string {
      const { cityname, stateabbr } = this.profileAccountData?.location || {};
      if (!cityname || !stateabbr) return '';
      const distanceFrom = this.profileAccountData?.distanceFrom;
      return distanceFrom ? `${distanceFrom} mi • ${cityname}, ${stateabbr}` : `${cityname}, ${stateabbr}`;
    },
    rateDisplay(): string {
      const convertedRate = (rate) => Math.ceil(rate / 100);

      if (this.paramData.careType === 'pet_care') {
        const { visitRate } = this.profileData?.rates || {};
        const rateText = convertedRate(visitRate);
        if (!visitRate) return '';
        return `<span class="text-h4">${rateText}</span><span class="text-body">/visit</span>`;
      }

      if (this.paramData.careType === 'companion_care') {
        const { fullDay } = this.profileData?.rates?.homeCareRates || {};
        const rateText = convertedRate(fullDay);
        if (!fullDay) return '';
        return `<span class="text-h4">${rateText}</span><span class="text-body">/day</span>`;
      }

      const { min, max, timeUnit } = this.profileData?.payRate || {};
      const rateText = `${convertedRate(min)}–${convertedRate(max)}`;
      const unit = timeUnit === 'hour' ? 'hr' : timeUnit;
      if (!min || !max) return '';
      return `<span class="text-h4">${rateText}</span><span class="text-body text-grey-text-subdued align-top">/${unit}</span>`;
    },
    loggedInDisplay(): string {
      const { lastLogin } = this.profileAccountData;
      if (!lastLogin) return '';
      return `Last login ${lastLogin.toLowerCase()}`;
    },
  },
  watch: {
    profileAccountData: {
      handler(newVal) {
        this.favoritedSitter = newVal.favoritedSitter;
      },
      deep: true,
    },
  },
  methods: {
    favoritedSitterChange(favorited: boolean): void {
      this.favoritedSitter = favorited;
    },
  },
};
</script>
